import { create } from "zustand";
import { persist } from "zustand/middleware";
import * as ApiService from "@/services/ApiService";
import { useProfileStore, getFacilityKey, getPatientKey } from "./profile";
import type { ITaskJSONFormQueryData } from "@/types/api";
import type { CaseHistoryTaskAction, FvTaskAction, PortalConfigMeta } from "@/types/task-actions";
import type { WizardStep } from "@/hooks/useWizardFlow";
import { SESSION_PORTAL_CONFIG_RESET_PENDING } from "@/constants/portalConfigSession";

interface CaseHistory {
  id: number;
  problems: unknown[];
  oats: unknown[];
  complaintFormValues: unknown | null;
  autoAccident: unknown[];
  aaFormValues: unknown | null;
}

interface Access {
  isAccessOnly: boolean;
  accessOnlyRoute: string;
}

export interface TaskForm {
  facilityFormID: number;
  action: string;
  formName: string;
  data: unknown;
  formData?: ITaskJSONFormQueryData;
}

type TaskTypePayload =
  | { back: true; removePage?: string; page?: string }
  | string;

interface TaskState {
  previousTaskType: string[];
  currentTaskType: string;
  caseHistory: CaseHistory;
  officeName: string;
  taskCode: string;
  access: Access;
  taskInfo: string | Record<string, unknown>;
  taskItem: unknown;
  urllinkInfo: unknown;
  isLoading: boolean;
  forms: TaskForm[];
  currentFormData: unknown;
  /** Loaded from GET GetCaseHistoryTaskAction when user opens an isHistory task. */
  caseHistoryTaskAction: CaseHistoryTaskAction | null;
  /** Portal task id for UpdateTaskComplete after portal case-history flow. */
  activePortalTaskId: number | null;
  /** True when user entered /complaint from dashboard isHistory task (portal config wire at end). */
  portalCaseHistoryFlow: boolean;
  /** Last GET GetFvTaskAction result when opening an FV task. */
  fvTaskAction: FvTaskAction | null;
  /** Parsed metadata from the PortalConfig JSON wire template. */
  portalConfigMeta: PortalConfigMeta | null;
  /** Computed wizard steps for the new-patient flow. */
  wizardSteps: WizardStep[];
  setCaseHistoryTaskAction: (payload: CaseHistoryTaskAction | null) => void;
  setActivePortalTaskId: (id: number | null) => void;
  setPortalCaseHistoryFlow: (v: boolean) => void;
  setFvTaskAction: (payload: FvTaskAction | null) => void;
  setPortalConfigMeta: (meta: PortalConfigMeta | null) => void;
  setWizardSteps: (steps: WizardStep[]) => void;
  setCurrentTaskType: (payload: string | TaskTypePayload) => void;
  setCurrentCaseHistoryId: (payload: number) => void;
  setTaskCurrentCaseHistoryProblems: (payload: unknown[]) => void;
  setTaskCurrentCaseHistoryOats: (payload: unknown[]) => void;
  setTaskCurrentCaseHistoryComplaintFormVal: (payload: unknown) => void;
  setTaskCurrentCaseHistoryAutoAccident: (payload: unknown[]) => void;
  setTaskCurrentCaseHistoryAAFormValues: (payload: unknown) => void;
  setTaskOfficeName: (payload: string) => void;
  setTaskIsAccessOnly: (payload: string | boolean) => void;
  setTaskIsAccessOnlyRoute: (payload: string) => void;
  setTaskInfo: (payload: string | Record<string, unknown>) => void;
  setTaskItem: (payload: unknown) => void;
  setUrlLinkInfo: (payload: unknown) => void;
  setTaskCode: (code: string) => void;
  /** Clear persisted case history when switching patients (id must not leak across patients). */
  resetCaseHistory: () => void;
  fetchTaskForms: (
    facilityId?: number | string,
    patientId?: number | string,
    facilityKey?: string
  ) => Promise<TaskForm[] | null>;
  fetchTaskFormData: (
    facilityFormID: string | number,
    patientID?: string | number
  ) => Promise<ITaskJSONFormQueryData | null>;
  clearForms: () => void;
}

const defaultCaseHistory: CaseHistory = {
  id: 0,
  problems: [],
  oats: [],
  complaintFormValues: null,
  autoAccident: [],
  aaFormValues: null,
};

function getPatientId(): number {
  return useProfileStore.getState().currentPatient?.patientID ?? 0;
}

function getFacilityId(): number {
  return useProfileStore.getState().currentPatient?.facilityID ?? 0;
}

export const useTaskStore = create<TaskState>()(
  persist(
    (set, get) => ({
      previousTaskType: [],
      currentTaskType: "",
      caseHistory: defaultCaseHistory,
      officeName: "",
      taskCode: "",
      access: { isAccessOnly: false, accessOnlyRoute: "/" },
      taskInfo: "",
      taskItem: undefined,
      urllinkInfo: undefined,
      isLoading: false,
      forms: [],
      currentFormData: undefined,
      caseHistoryTaskAction: null,
      activePortalTaskId: null,
      portalCaseHistoryFlow: false,
      fvTaskAction: null,
      portalConfigMeta: null,
      wizardSteps: [],

      setCaseHistoryTaskAction: (payload) => set({ caseHistoryTaskAction: payload }),
      setActivePortalTaskId: (id) => set({ activePortalTaskId: id }),
      setPortalCaseHistoryFlow: (v) => set({ portalCaseHistoryFlow: v }),
      setFvTaskAction: (payload) => set({ fvTaskAction: payload }),
      setPortalConfigMeta: (meta) => set({ portalConfigMeta: meta }),
      setWizardSteps: (steps) => set({ wizardSteps: steps }),

      setCurrentTaskType: (payload) => {
        const state = get();
        if (
          typeof payload === "object" &&
          payload !== null &&
          "back" in payload &&
          payload.back
        ) {
          set({
            previousTaskType: state.previousTaskType.filter(
              (c) => c !== payload.removePage
            ),
            currentTaskType: payload.page ?? "",
          });
        } else {
          const nextPage =
            typeof payload === "string"
              ? payload
              : (payload as { page?: string }).page ?? "";
          set({
            previousTaskType: [...state.previousTaskType, state.currentTaskType],
            currentTaskType: nextPage,
          });
        }
      },

      setCurrentCaseHistoryId: (payload) =>
        set((s) => ({
          caseHistory: { ...s.caseHistory, id: payload },
        })),

      setTaskCurrentCaseHistoryProblems: (payload) =>
        set((s) => ({
          caseHistory: { ...s.caseHistory, problems: payload },
        })),

      setTaskCurrentCaseHistoryOats: (payload) =>
        set((s) => ({
          caseHistory: { ...s.caseHistory, oats: payload },
        })),

      setTaskCurrentCaseHistoryComplaintFormVal: (payload) =>
        set((s) => ({
          caseHistory: { ...s.caseHistory, complaintFormValues: payload },
        })),

      setTaskCurrentCaseHistoryAutoAccident: (payload) =>
        set((s) => ({
          caseHistory: { ...s.caseHistory, autoAccident: payload },
        })),

      setTaskCurrentCaseHistoryAAFormValues: (payload) =>
        set((s) => ({
          caseHistory: { ...s.caseHistory, aaFormValues: payload },
        })),

      setTaskOfficeName: (payload) => set({ officeName: payload }),
      setTaskIsAccessOnly: (payload) =>
        set((s) => ({
          access: { ...s.access, isAccessOnly: !!payload },
        })),
      setTaskIsAccessOnlyRoute: (payload) =>
        set((s) => ({
          access: { ...s.access, accessOnlyRoute: payload },
        })),
      setTaskInfo: (payload) => set({ taskInfo: payload }),
      setTaskItem: (payload) => set({ taskItem: payload }),
      setUrlLinkInfo: (payload) => set({ urllinkInfo: payload }),
      setTaskCode: (code) => set({ taskCode: code }),

      resetCaseHistory: () => {
        try {
          sessionStorage.removeItem(SESSION_PORTAL_CONFIG_RESET_PENDING);
        } catch {
          /* ignore */
        }
        set({
          caseHistory: { ...defaultCaseHistory },
          caseHistoryTaskAction: null,
          activePortalTaskId: null,
          portalCaseHistoryFlow: false,
          fvTaskAction: null,
          portalConfigMeta: null,
          wizardSteps: [],
        });
      },

      fetchTaskForms: async (
        _facilityId = getFacilityId(),
        _patientId = getPatientId(),
        facilityKey = getFacilityKey()
      ) => {
        set({ isLoading: true });
        try {
          const response = await ApiService.post("/Portal/GetForms", {
            facilityKey: facilityKey || getFacilityKey(),
            patientKey: getPatientKey(),
          });
          const forms = response.data as TaskForm[];
          set({ forms });
          return forms;
        } catch (error) {
          console.error("Error task store fetchTaskForms:", error);
          return null;
        } finally {
          set({ isLoading: false });
        }
      },

      clearForms: () => set({ forms: [] }),

      fetchTaskFormData: async (
        facilityFormID: string | number,
        patientID = getPatientId()
      ) => {
        set({ isLoading: true });
        try {
          const response = await ApiService.post("/Form/FacilityFormJson", {
            facilityFormID,
            patientID,
          });
          const data = response.data as ITaskJSONFormQueryData;
          set({ currentFormData: data });
          return data;
        } catch (error) {
          console.error("Error task store fetchTaskFormData:", error);
          return null;
        } finally {
          set({ isLoading: false });
        }
      },
    }),
    { name: "portal-task" }
  )
);
